import { createTheme } from '@mui/material/styles';

export const THEME_MODE_STORAGE_KEY = 'themeMode';

const lightPalette = {
  mode: 'light',
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828',
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100',
  },
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b',
  },
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20',
  },
  incoming: {
    main: '#dfc968',
    light: '#fbbf24',
    dark: '#94881a',
  },
  overdue: {
    main: '#c62828',
    light: '#e57373',
    dark: '#8e0000',
  },
  chart: {
    successfulResults: '#4f9b7bff',
    unsuccessfulResults: '#d32f2f',
  },
  fieldType: {
    boolean: '#2e7d32',
    number: '#0288d1',
    text: '#b07400',
    select: '#7e57c2',
    radio: '#2e7d32',
    unchecked: '#8a8a8a',
  },
  primary: {
    main: '#4f9b7bff',
    white: '#FFFFFF',
    black: '#000000',
    gray: '#fafbfc',
  },
  secondary: {
    main: '#000000',
  },
  logo: {
    main: '#4f9b7bff',
  },
  background: {
    default: '#F5F5F5',
    paper: '#FFFFFF',
  },
  link: {
    main: '#4f9b7bff',
    menu: '#000',
  },
  action: {
    hover: '#ebebeb',
    hoverDark: '#000',
    lightHover: '#f7f7f7f3',
  },
  menu: {
    selected: '#ecf4f0',
    selectedSpan: '#4f9b7bff',
  },
  custom: {
    darkGreen: '#18221d',
    lightGreen: '#5f6f66',
    whiteGreen: '#dfe8e3',
    shadow: '#abb4b0',
  },
  notification: {
    space: '#7e57c2',
    inspection: '#1976d2',
    dueSoon: '#f9a825',
    overdue: '#d32f2f',
    limitReached: '#ef6c00',
    unreadDot: '#4f9b7bff',
    unreadBackground: '#d1ebdc',
  },
};

const darkPalette = {
  mode: 'dark',
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
  },
  warning: {
    main: '#ffa726',
    light: '#ffb74d',
    dark: '#f57c00',
  },
  info: {
    main: '#29b6f6',
    light: '#4fc3f7',
    dark: '#0288d1',
  },
  success: {
    main: '#66bb6a',
    light: '#81c784',
    dark: '#388e3c',
  },
  incoming: {
    main: '#fbbf24',
    light: '#fde047',
    dark: '#d97706',
  },
  overdue: {
    main: '#e57373',
    light: '#ef9a9a',
    dark: '#c62828',
  },
  chart: {
    successfulResults: '#6fbfa0',
    unsuccessfulResults: '#ef5350',
  },
  fieldType: {
    boolean: '#66bb6a',
    number: '#29b6f6',
    text: '#ffb74d',
    select: '#b39ddb',
    radio: '#66bb6a',
    unchecked: '#9e9e9e',
  },
  primary: {
    main: '#6fbfa0',
    white: '#1e1e1e',
    black: '#FFFFFF',
    gray: '#2a2a2a',
  },
  secondary: {
    main: '#FFFFFF',
  },
  logo: {
    main: '#6fbfa0',
  },
  background: {
    default: '#121212',
    paper: '#1e1e1e',
  },
  link: {
    main: '#6fbfa0',
    menu: '#FFFFFF',
  },
  action: {
    hover: '#2a2a2a',
    hoverDark: '#FFFFFF',
    lightHover: '#1a1a1a',
  },
  menu: {
    selected: '#2a3a33',
    selectedSpan: '#6fbfa0',
  },
  custom: {
    darkGreen: '#dfe8e3',
    lightGreen: '#a8b8b0',
    whiteGreen: '#2a3a33',
    shadow: '#000000',
  },
  notification: {
    space: '#b39ddb',
    inspection: '#64b5f6',
    dueSoon: '#ffd54f',
    overdue: '#ef5350',
    limitReached: '#ffa726',
    unreadDot: '#6fbfa0',
    unreadBackground: '#1f2a25',
  },
};

export const createAppTheme = (mode) => {
  const isDark = mode === 'dark';
  const inputBackground = isDark ? '#2a2a2a' : '#FFFFFF';

  const shadow = {
    card: isDark
      ? '0 1px 2px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.4)'
      : '0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.10)',
    popover: isDark
      ? '0 8px 24px rgba(0,0,0,0.6), 0 2px 6px rgba(0,0,0,0.5)'
      : '0 8px 24px rgba(16,24,40,0.12), 0 2px 6px rgba(16,24,40,0.08)',
  };

  return createTheme({
    typography: {
      fontFamily: ['Noto Sans', 'Arial', 'sans-serif'].join(','),
      fontSize: 14,
      fontSizeMedium: 16,
      fontSizeTitle: 24,
      fontSizeBig: 26,
      fontSizeVeryBig: 40,
      fontSizeSmall: 12,
      lineHeight: 1.6,
    },
    shadow,
    palette: isDark ? darkPalette : lightPalette,
    padding: {
      small: '5px',
      content: '10px',
      container: '70px',
      containerBig: '50px',
      section: '20px',
      footer: '20px',
      sideMenuButton: '65px',
      sideMenuButtonHidden: '30px',
    },
    radius: {
      card: '8px',
    },
    height: {
      topMenu: '60px',
      footer: '100px',
      notificationDropdown: '480px',
    },
    width: {
      sideMenu: '300px',
      sideMenuLeft: '230px',
      filterDrawer: '450px',
      form: '600px',
      formMin: '300px',
      topMenuLogo: '40px',
      descriptionItem: '300px',
      limitedSpan: '200px',
      notificationDropdown: '380px',
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiContainer: {
        styleOverrides: {
          root: {
            '&.MuiContainer-maxWidthXl': {
              maxWidth: '1450px',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
          contained: {
            '& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-endIcon .MuiSvgIcon-root': {
              color: 'inherit',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
        },
        variants: [
          {
            props: { color: 'overdue' },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.overdue.main,
              color: theme.palette.getContrastText(theme.palette.overdue.main),
            }),
          },
          {
            props: { color: 'incoming' },
            style: ({ theme }) => ({
              backgroundColor: theme.palette.incoming.main,
              color: theme.palette.getContrastText(theme.palette.incoming.main),
            }),
          },
        ],
      },
      MuiInputLabel: {
        styleOverrides: {
          outlined: ({ theme }) => ({
            '&.MuiInputLabel-shrink': {
              [theme.breakpoints.down('lg')]: {
                backgroundColor: inputBackground,
                paddingLeft: 4,
                paddingRight: 4,
                zIndex: 1,
              },
            },
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
            boxShadow: shadow.popover,
            border: `1px solid ${(isDark ? darkPalette : lightPalette).divider}`,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: inputBackground,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: inputBackground,
            '&:hover': {
              backgroundColor: inputBackground,
            },
            '&.Mui-focused': {
              backgroundColor: inputBackground,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: inputBackground,
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            ...(isDark && { color: darkPalette.secondary.main }),
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            fontSize: theme.typography.fontSize,
          }),
        },
      },
    },
  });
};

const getStoredThemeMode = () => {
  const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
  return stored === 'dark' ? 'dark' : 'light';
};

const theme = createAppTheme(getStoredThemeMode());

export default theme;
